import React, { useEffect, useRef, useState } from 'react'
import {
  Box,
  CircularProgress,
  Stack,
  styled,
  Typography,
  TypographyProps,
  useTheme,
} from '@mui/material'
import DOMPurify from 'dompurify'
import { marked } from 'marked'
import { motion } from 'framer-motion'
import { IoSend } from 'react-icons/io5'
import { useTranslation } from 'react-i18next'
import { type MessageWithStream } from '@plug-core/utils/llmChat'
import useLlmChat from '@plug-core/hooks/useLlmChat'
import sparklesAnimation from '@assets/lottie/sparkles.json'
import RainbowTextField from '@components/RainbowTextField/RainbowTextField'
import Touchable from '@components/Touchable'
import { AnimationPlayer } from '@components/AnimationPlayer'
import Button from '@components/Button'

const MessageContent = styled(Typography)<TypographyProps>(() => ({
  'ol, ul': {
    paddingInlineStart: '24px',
  },
}))

const ChatMessage = ({
  message,
  updateMessageContent,
  markAsDone,
}: {
  message: MessageWithStream
  updateMessageContent: (content: string) => void
  markAsDone: () => void
}) => {
  const messageContentRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const getStream = async () => {
      if (message.stream) {
        const decoder = new TextDecoder()
        const { body } = await message.stream

        for await (const part of body) {
          if (typeof part === 'string') {
            updateMessageContent(part)
          } else {
            updateMessageContent(decoder.decode(part))
          }
        }

        markAsDone()
      }
    }

    getStream()
  }, [message.stream])

  useEffect(() => {
    if (!messageContentRef.current) return

    messageContentRef.current.addEventListener('click', (e) => {
      const potentialLink = e.target as HTMLLinkElement

      if (potentialLink?.nodeName === 'A') {
        e.preventDefault()
        window.open(potentialLink.href, '_blank')
      }
    })
  }, [messageContentRef])

  const content = DOMPurify.sanitize(marked.parse(message.content) as string)

  return (
    <Box
      bgcolor={message.role === 'user' ? 'white' : 'background.paper'}
      px={1.5}
      borderRadius={1.5}
      mr={message.role === 'user' ? 2 : 'auto'}
      ml={message.role === 'assistant' ? 2 : 'auto'}
      my={1}
      maxWidth="75%"
    >
      <MessageContent
        color={message.role === 'user' ? 'primary.contrastText' : 'text.primary'}
        sx={{ wordBreak: 'break-word', textWrap: 'wrap', userSelect: 'text' }}
        dangerouslySetInnerHTML={content ? { __html: content } : undefined}
        component="div"
        ref={messageContentRef}
      >
        {content ? undefined : <PendingMessageContent />}
      </MessageContent>
    </Box>
  )
}

const PendingMessageContent = () => {
  const { palette } = useTheme()

  const [highlightIndex, setHighlightIndex] = useState(0)

  useEffect(() => {
    setInterval(() => {
      setHighlightIndex((i) => {
        if (i === 4) {
          return 0
        } else {
          return i + 1
        }
      })
    }, 300)
  }, [])

  const getStyle = (idx: number) => {
    if (highlightIndex === idx) {
      return { color: palette.text.primary, fontSize: '24px' }
    } else {
      return { color: palette.text.secondary, fontSize: '18px' }
    }
  }

  return (
    <Stack flexDirection="row" width={24} py={1.5}>
      <motion.span animate={getStyle(0)}>•</motion.span>
      <motion.span animate={getStyle(1)}>•</motion.span>
      <motion.span animate={getStyle(2)}>•</motion.span>
    </Stack>
  )
}

export default () => {
  const {
    messages,
    question,
    setQuestion,
    submitQuestion,
    bubbles,
    isWriting,
    disabled,
    updateMessage,
  } = useLlmChat()

  const { t } = useTranslation()
  const scrollView = useRef<HTMLDivElement>(null)

  useEffect(() => {
    scrollView.current!.scrollTo(0, scrollView.current!.scrollHeight)
  }, [messages.length])

  return (
    <Stack flexDirection="column" height="calc(100% - 65px)">
      <Stack
        flexDirection="column-reverse"
        height="calc(100% - 80px)"
        overflow="scroll"
        ref={scrollView}
      >
        {[...messages].reverse().map((msg) => (
          <ChatMessage
            key={msg.id}
            message={msg}
            updateMessageContent={(content) =>
              updateMessage(msg.id, (m) => ({ content: m.content + content }))
            }
            markAsDone={() => updateMessage(msg.id, { doneStreaming: true })}
          />
        ))}
      </Stack>

      {messages.length < 2 && !isWriting && (
        <Stack mx={2.5} spacing={0.75} mt={1} alignItems="flex-end">
          {bubbles.map((bubble) => (
            <Button
              sx={{ textTransform: 'none', fontWeight: 400 }}
              variant="outlined"
              key={bubble.message}
              onClick={() => submitQuestion(bubble.message)}
            >
              {bubble.message} {bubble.emoji}
            </Button>
          ))}
        </Stack>
      )}

      <Stack mx={2} flexDirection="row" py={2} alignItems="center">
        <Box flexGrow={1}>
          <RainbowTextField
            placeholder={t('llmChat.inputPlaceholder')}
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && submitQuestion(question)}
            autoFocus
          />
        </Box>

        <Touchable
          style={{
            borderRadius: 14,
            background:
              'linear-gradient(107deg, rgba(30,33,105,1) 0%, rgba(68,50,140,1) 50%, rgba(28,15,80,1) 100%)',
            position: 'relative',
            marginLeft: 12,
            filter: disabled ? 'brightness(0.7)' : undefined,
            width: 48,
            height: 48,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: isWriting ? 'progress' : disabled ? 'default' : 'pointer',
            overflow: 'hidden',
          }}
          scaleFactor={0.1}
          onClick={() => submitQuestion(question)}
          disabled={disabled}
        >
          {isWriting ? <CircularProgress size={20} thickness={5} /> : <IoSend size={20} />}

          {!disabled && (
            <Box
              width="100px"
              top="-11px"
              left="-29px"
              position="absolute"
              sx={{ filter: 'opacity(0.75)' }}
            >
              <AnimationPlayer animationData={sparklesAnimation} />
            </Box>
          )}
        </Touchable>
      </Stack>
    </Stack>
  )
}
